import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link } from 'react-router-dom';

import AcademiaSelect from '../../common/AcademiaSelect';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import { AcademicPanelCard, AcademicPanelTableWrap } from './AcademicPanelCard';
import {
  buildParrainageRegleDrafts,
  useSystemSettings,
  type ParrainageRegleDraft,
} from '../../../hooks/useSystemSettings';
import {
  normalizePlatformSocialLinks,
  PLATFORM_SOCIAL_NETWORKS,
} from '../../../config/platformSocialNetworks';
import type {
  AdminParrainageRegle,
  AdminSystemCurrencySettings,
  AdminSystemPlatform,
  AdminSystemSettingsData,
  PlatformSocialNetwork,
} from '../../../types/admin';
import { resolveBrandingLogoUrl } from '../../../utils/brandingUrl';
import { displayPublicSiteHost } from '../../../utils/publicSiteOrigin';
import { downloadUserGuidePdf } from '../../../services/academiaAdminService';

const fieldClass =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

const tarifInputClass =
  'tarif-field__input w-full min-w-0 rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm tabular-nums text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

const textareaClass = `${fieldClass} min-h-[220px] resize-y font-mono text-[13px] leading-relaxed`;

type SettingsSection = 'identite' | 'contact' | 'social' | 'legal' | 'devise' | 'metier' | 'documentation';

const SECTIONS: { id: SettingsSection; label: string; icon: string }[] = [
  { id: 'identite', label: 'Identité', icon: 'public' },
  { id: 'contact', label: 'Contact', icon: 'mail' },
  { id: 'social', label: 'Réseaux sociaux', icon: 'share_2' },
  { id: 'legal', label: 'CGU & confidentialité', icon: 'gavel' },
  { id: 'devise', label: 'Devise & taux', icon: 'payments' },
  { id: 'metier', label: 'Paramètres métier', icon: 'tune' },
  { id: 'documentation', label: 'Documentation', icon: 'menu_book' },
];

function platformFromApi(platform: AdminSystemPlatform): AdminSystemPlatform {
  return {
    ...platform,
    supportPhone: platform.supportPhone ?? '',
    socialLinks: normalizePlatformSocialLinks(platform.socialLinks),
  };
}

function formatShortDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatRate(value: number | string | undefined, options: Intl.NumberFormatOptions = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('fr-FR', options);
}

function ParrainageRegleRow({
  regle,
  draft,
  onDraftChange,
}: {
  regle: AdminParrainageRegle;
  draft: ParrainageRegleDraft;
  onDraftChange: (next: ParrainageRegleDraft) => void;
}) {
  return (
    <tr>
      <td className="font-medium text-slate-900">{regle.typeProfilLabel}</td>
      <td>
        <input
          type="number"
          min={1}
          step={1}
          value={draft.seuilFilleuls}
          onChange={(e) => onDraftChange({ ...draft, seuilFilleuls: e.target.value })}
          className={`${tarifInputClass} max-w-[8rem]`}
        />
      </td>
      <td>
        <div className="flex max-w-[9rem] items-center gap-2">
          <input
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={draft.tauxCommissionPct}
            onChange={(e) => onDraftChange({ ...draft, tauxCommissionPct: e.target.value })}
            className={tarifInputClass}
          />
          <span className="text-sm font-semibold text-on-surface-variant">%</span>
        </div>
      </td>
    </tr>
  );
}

interface Props {
  user: {
    id?: string;
    role?: string;
  };
}

export default function AcademiaApplicationSettingsPanel({ user }: Props) {
  const {
    data,
    isLoading,
    isError,
    isSaving,
    isUploadingLogo,
    saveError,
    refetch,
    saveApplicationSettings,
    uploadPlatformLogo,
    clearPlatformLogo,
  } = useSystemSettings(user);

  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const [activeSection, setActiveSection] = useState<SettingsSection>('identite');
  const [form, setForm] = useState<AdminSystemPlatform | null>(null);
  const [currencyForm, setCurrencyForm] = useState<AdminSystemCurrencySettings | null>(null);
  const [cguContent, setCguContent] = useState('');
  const [confidentialiteContent, setConfidentialiteContent] = useState('');
  const [referralBonus, setReferralBonus] = useState('');
  const [regleDrafts, setRegleDrafts] = useState<Record<string, ParrainageRegleDraft>>({});
  const [recalculateTarifs, setRecalculateTarifs] = useState(false);
  const [snapshot, setSnapshot] = useState<AdminSystemSettingsData | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');
  const [isDownloadingUserGuide, setIsDownloadingUserGuide] = useState(false);

  useRegisterPortalRefresh(() => void refetch());

  useEffect(() => {
    if (!data) return;
    setForm(platformFromApi(data.platform));
    setCurrencyForm(data.currency);
    setCguContent(data.legal.cgu.content);
    setConfidentialiteContent(data.legal.confidentialite.content);
    setReferralBonus(String(data.parrainage.bonusInscriptionCdf ?? 0));
    setRegleDrafts(buildParrainageRegleDrafts(data.parrainage.regles));
    setRecalculateTarifs(false);
    setSnapshot(data);
  }, [data]);

  const isDirty = useMemo(() => {
    if (!form || !snapshot || !currencyForm) return false;
    const p = snapshot.platform;
    const platformDirty =
      form.appName !== p.appName ||
      form.slogan !== p.slogan ||
      form.supportEmail !== p.supportEmail ||
      (form.supportPhone ?? '') !== (p.supportPhone ?? '') ||
      JSON.stringify(form.socialLinks) !== JSON.stringify(normalizePlatformSocialLinks(p.socialLinks)) ||
      form.logoUrl !== p.logoUrl ||
      (form.publicSiteUrl ?? '') !== (p.publicSiteUrl ?? '') ||
      form.maintenanceMode !== p.maintenanceMode ||
      form.pushNotifications !== p.pushNotifications ||
      form.soundAlerts !== p.soundAlerts ||
      form.autoDiagnostics !== p.autoDiagnostics;
    const legalDirty =
      cguContent !== snapshot.legal.cgu.content ||
      confidentialiteContent !== snapshot.legal.confidentialite.content;
    const currencyDirty =
      currencyForm.displayMode !== snapshot.currency.displayMode ||
      Number(currencyForm.usdToCdfRate) !== Number(snapshot.currency.usdToCdfRate) ||
      Number(currencyForm.cdfToUsdRate) !== Number(snapshot.currency.cdfToUsdRate);
    const parrainageDirty =
      referralBonus !== String(snapshot.parrainage.bonusInscriptionCdf ?? 0) ||
      snapshot.parrainage.regles.some((regle) => {
        const draft = regleDrafts[regle.id] ?? {
          seuilFilleuls: String(regle.seuilFilleuls),
          tauxCommissionPct: String(regle.tauxCommissionPct),
        };
        return (
          Number(draft.seuilFilleuls) !== regle.seuilFilleuls ||
          Number(draft.tauxCommissionPct) !== regle.tauxCommissionPct
        );
      });
    return platformDirty || legalDirty || currencyDirty || parrainageDirty;
  }, [form, snapshot, cguContent, confidentialiteContent, currencyForm, referralBonus, regleDrafts]);

  const updateField = useCallback(<K extends keyof AdminSystemPlatform>(key: K, value: AdminSystemPlatform[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const updateSocialLink = useCallback((network: PlatformSocialNetwork, value: string) => {
    setForm((prev) =>
      prev
        ? {
            ...prev,
            socialLinks: {
              ...prev.socialLinks,
              [network]: value,
            },
          }
        : prev,
    );
  }, []);

  const handleCancel = useCallback(() => {
    if (!snapshot) return;
    setForm(platformFromApi(snapshot.platform));
    setCurrencyForm(snapshot.currency);
    setCguContent(snapshot.legal.cgu.content);
    setConfidentialiteContent(snapshot.legal.confidentialite.content);
    setReferralBonus(String(snapshot.parrainage.bonusInscriptionCdf ?? 0));
    setRegleDrafts(buildParrainageRegleDrafts(snapshot.parrainage.regles));
    setRecalculateTarifs(false);
  }, [snapshot]);

  const handleLogoFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      try {
        await uploadPlatformLogo(file);
        setToastVariant('success');
        setToastMessage('Logo mis à jour.');
      } catch {
        setToastVariant('error');
        setToastMessage('Téléversement du logo impossible.');
      }
    },
    [uploadPlatformLogo],
  );

  const handleDownloadUserGuide = useCallback(async () => {
    if (isDownloadingUserGuide) return;
    setIsDownloadingUserGuide(true);
    try {
      await downloadUserGuidePdf();
      setToastVariant('success');
      setToastMessage('Documentation utilisateur téléchargée.');
    } catch {
      setToastVariant('error');
      setToastMessage('Impossible de générer la documentation PDF.');
    } finally {
      setIsDownloadingUserGuide(false);
    }
  }, [isDownloadingUserGuide]);

  const handleSave = useCallback(async () => {
    if (!form || !snapshot || !currencyForm) return;
    try {
      await saveApplicationSettings({
        platform: form,
        cguContent,
        confidentialiteContent,
        initial: snapshot,
        currency: currencyForm,
        parrainageBonus: referralBonus,
        parrainageRegleDrafts: regleDrafts,
        recalculateTarifs,
      });
      setToastVariant('success');
      setToastMessage('Paramètres de l’application enregistrés.');
    } catch {
      setToastVariant('error');
      setToastMessage(saveError ?? 'Enregistrement impossible.');
    }
  }, [
    form,
    snapshot,
    currencyForm,
    cguContent,
    confidentialiteContent,
    referralBonus,
    regleDrafts,
    recalculateTarifs,
    saveApplicationSettings,
    saveError,
  ]);

  const sideCard = useMemo(
    () => ({
      label: 'Dernière mise à jour',
      value: form?.updatedAt ? formatShortDate(form.updatedAt) : '…',
      helper: form?.appName ? `Application : ${form.appName}` : null,
      sideLinks: [{ label: 'Site & QR', to: '/admin/site', icon: 'public' }],
    }),
    [form?.updatedAt, form?.appName],
  );

  if (user.role !== 'ADMINISTRATEUR') {
    return (
      <AdminPageShell
        title="Paramètres de l’application"
        description="Cette section est réservée aux comptes administrateur."
        isError
        errorMessage="Accès refusé."
      />
    );
  }

  const previewLogoSrc = resolveBrandingLogoUrl(form?.logoUrl);
  const activePublicSiteHost =
    snapshot && form
      ? displayPublicSiteHost(snapshot.platform.resolvedPublicSiteOrigin ?? '', form.publicSiteUrl)
      : '';

  const currencyLastUpdate = currencyForm?.updatedAt
    ? new Date(currencyForm.updatedAt).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  return (
    <>
      <AdminToast message={toastMessage} variant={toastVariant} onClose={() => setToastMessage(null)} />
      <AdminPageShell
        title="Paramètres de l’application"
        description="Identité publique, contact, documents légaux, devise, parrainage et réglages métier — tout est chargé depuis la base de données."
        sideCard={sideCard}
        onRetry={() => void refetch()}
        isLoading={isLoading || !form || !snapshot || !currencyForm}
        isError={isError}
        loadingMessage="Chargement des paramètres…"
        errorMessage="Impossible de charger la configuration."
      >
        {saveError ? (
          <div className="mb-4 rounded-lg border border-error/30 bg-error-container/40 px-4 py-2 text-sm text-error">
            {saveError}
          </div>
        ) : null}

        <nav
          aria-label="Sections des paramètres"
          className="mb-6 flex flex-wrap gap-2 rounded-xl border border-outline-variant/20 bg-white p-2 shadow-sm"
        >
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => setActiveSection(section.id)}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                activeSection === section.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:bg-surface-container-high'
              }`}
            >
              <LucideSymbolIcon name={section.icon} size={16} tone={activeSection === section.id ? 'inherit' : 'muted'} />
              {section.label}
            </button>
          ))}
        </nav>

        {activeSection === 'identite' ? (
          <div className="tarif-page-stack">
            <AcademicPanelCard
              icon="public"
              title="Identité de la plateforme"
              subtitle="Nom, slogan et logo affichés sur le site public et dans les e-mails."
            >
              <div className="grid gap-5 lg:grid-cols-2">
                <label className="tarif-field">
                  <span className="tarif-field__label">Nom de l’application</span>
                  <input
                    type="text"
                    className={fieldClass}
                    value={form?.appName ?? ''}
                    onChange={(e) => updateField('appName', e.target.value)}
                  />
                </label>
                <label className="tarif-field">
                  <span className="tarif-field__label">Slogan</span>
                  <input
                    type="text"
                    className={fieldClass}
                    value={form?.slogan ?? ''}
                    onChange={(e) => updateField('slogan', e.target.value)}
                  />
                </label>
              </div>

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-outline-variant/25 bg-surface-container-high">
                  {previewLogoSrc ? (
                    <img src={previewLogoSrc} alt="" className="max-h-full max-w-full object-contain p-2" />
                  ) : (
                    <LucideSymbolIcon name="image" tone="muted" size={32} />
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-3">
                  <label className="tarif-field">
                    <span className="tarif-field__label">Logo (URL)</span>
                    <input
                      type="url"
                      className={fieldClass}
                      value={form?.logoUrl ?? ''}
                      onChange={(e) => updateField('logoUrl', e.target.value.trim() === '' ? null : e.target.value)}
                      placeholder="https://…"
                    />
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <input
                      ref={logoFileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => void handleLogoFileChange(e)}
                    />
                    <button
                      type="button"
                      disabled={isUploadingLogo}
                      onClick={() => logoFileInputRef.current?.click()}
                      className="rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/5 disabled:opacity-60"
                    >
                      {isUploadingLogo ? 'Envoi…' : 'Téléverser un fichier'}
                    </button>
                    {form?.logoUrl ? (
                      <button
                        type="button"
                        disabled={isUploadingLogo}
                        onClick={() => void clearPlatformLogo()}
                        className="rounded-lg border border-outline-variant/30 px-3 py-2 text-sm font-semibold text-on-surface-variant hover:bg-surface-container-high disabled:opacity-60"
                      >
                        Supprimer le logo
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </AcademicPanelCard>

            <AcademicPanelCard
              icon="link"
              title="Site public"
              subtitle="Domaine utilisé pour les QR codes d’inscription et le parrainage."
            >
              <label className="tarif-field">
                <span className="tarif-field__label">URL publique</span>
                <input
                  type="text"
                  className={fieldClass}
                  value={form?.publicSiteUrl ?? ''}
                  onChange={(e) =>
                    updateField('publicSiteUrl', e.target.value.trim() === '' ? null : e.target.value)
                  }
                  placeholder="https://academia.jconsult.cd"
                />
                <span className="mt-1 text-xs text-on-surface-variant">
                  {activePublicSiteHost ? `Domaine actif : ${activePublicSiteHost}` : 'Variable d’environnement serveur utilisée si vide.'}
                </span>
              </label>
              <p className="mt-4 text-sm text-on-surface-variant">
                Prévisualisez les QR codes sur la page{' '}
                <Link to="/admin/site" className="font-semibold text-primary hover:underline">
                  Site & QR
                </Link>
                .
              </p>
            </AcademicPanelCard>
          </div>
        ) : null}

        {activeSection === 'contact' ? (
          <AcademicPanelCard
            icon="support_agent"
            title="Coordonnées de contact"
            subtitle="E-mail et téléphone affichés sur le site public et dans l’assistance."
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <label className="tarif-field">
                <span className="tarif-field__label">E-mail de support</span>
                <input
                  type="email"
                  className={fieldClass}
                  value={form?.supportEmail ?? ''}
                  onChange={(e) => updateField('supportEmail', e.target.value)}
                  placeholder="academia@jconsultmy.com"
                />
              </label>
              <label className="tarif-field">
                <span className="tarif-field__label">Téléphone de la plateforme</span>
                <input
                  type="tel"
                  className={fieldClass}
                  value={form?.supportPhone ?? ''}
                  onChange={(e) => updateField('supportPhone', e.target.value)}
                  placeholder="+243 000 000 000"
                />
                <span className="mt-1 text-xs text-on-surface-variant">
                  Format libre — affiché tel quel sur la page d’accueil et le pied de page.
                </span>
              </label>
            </div>
          </AcademicPanelCard>
        ) : null}

        {activeSection === 'social' ? (
          <AcademicPanelCard
            icon="share"
            title="Réseaux sociaux"
            subtitle="Liens affichés dans le pied de page et les sections « Suivez-nous ». Laissez vide pour masquer un réseau."
          >
            <div className="grid gap-5 lg:grid-cols-2">
              {PLATFORM_SOCIAL_NETWORKS.map((network) => (
                <label key={network.id} className="tarif-field">
                  <span className="tarif-field__label">{network.label}</span>
                  <input
                    type="url"
                    className={fieldClass}
                    value={form?.socialLinks?.[network.id] ?? ''}
                    onChange={(e) => updateSocialLink(network.id, e.target.value)}
                    placeholder={network.placeholder}
                  />
                </label>
              ))}
            </div>
          </AcademicPanelCard>
        ) : null}

        {activeSection === 'legal' ? (
          <div className="tarif-page-stack">
            <AcademicPanelCard
              icon="description"
              title="Conditions générales d’utilisation"
              subtitle={`Dernière mise à jour : ${formatShortDate(snapshot?.legal.cgu.updatedAt)}`}
            >
              <textarea
                className={textareaClass}
                value={cguContent}
                onChange={(e) => setCguContent(e.target.value)}
                placeholder="Saisissez le contenu des CGU…"
              />
              <p className="mt-3 text-xs text-on-surface-variant">
                Page publique :{' '}
                <a href="/legal/cgu" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">
                  /legal/cgu
                </a>
              </p>
            </AcademicPanelCard>

            <AcademicPanelCard
              icon="shield"
              title="Politique de confidentialité"
              subtitle={`Dernière mise à jour : ${formatShortDate(snapshot?.legal.confidentialite.updatedAt)}`}
            >
              <textarea
                className={textareaClass}
                value={confidentialiteContent}
                onChange={(e) => setConfidentialiteContent(e.target.value)}
                placeholder="Saisissez la politique de confidentialité…"
              />
              <p className="mt-3 text-xs text-on-surface-variant">
                Page publique :{' '}
                <a
                  href="/legal/confidentialite"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary hover:underline"
                >
                  /legal/confidentialite
                </a>
              </p>
            </AcademicPanelCard>
          </div>
        ) : null}

        {activeSection === 'devise' && currencyForm ? (
          <div className="tarif-page-stack">
            <div className="tarif-info-banner">
              <span className="tarif-info-banner__icon" aria-hidden>
                <LucideSymbolIcon name="info" tone="inherit" size={20} />
              </span>
              <p>
                Tous les montants facturés sont en <strong>franc congolais (CDF)</strong>. Les références USD des tarifs
                peuvent être recalculées lors d’un changement de taux — voir aussi la page{' '}
                <Link to="/admin/tarifs" className="font-bold text-primary hover:underline">
                  Tarification
                </Link>
                .
              </p>
            </div>

            <AcademicPanelCard
              icon="payments"
              title="Configuration CDF"
              subtitle={`Dernière mise à jour : ${currencyLastUpdate}`}
              variant="accent"
              badge={
                <span className="academic-panel-badge">
                  {currencyForm.displayMode === 'DOUBLE_AFFICHAGE' ? 'Double affichage' : 'Devise principale (CDF)'}
                </span>
              }
            >
              <div className="currency-rate-chips">
                <div className="currency-rate-chip">
                  <p className="currency-rate-chip__label">Taux USD → CDF</p>
                  <p className="currency-rate-chip__value">
                    {formatRate(currencyForm.usdToCdfRate)} <span className="text-sm font-semibold">CDF</span>
                  </p>
                </div>
                <div className="currency-rate-chip currency-rate-chip--muted">
                  <p className="currency-rate-chip__label">Taux CDF → USD</p>
                  <p className="currency-rate-chip__value">
                    {formatRate(currencyForm.cdfToUsdRate, { maximumFractionDigits: 6 })}
                  </p>
                </div>
              </div>

              <p className="tarif-subsection-title">Paramètres</p>

              <div className="mb-4">
                <span className="tarif-field__label">Devise de facturation</span>
                <div className="mt-1">
                  <span className="currency-devise-badge">
                    <LucideSymbolIcon name="payments" tone="inherit" size={18} />
                    CDF — Franc congolais
                  </span>
                </div>
              </div>

              <div className="tarif-field-grid">
                <label className="tarif-field">
                  <span className="tarif-field__label">Mode d&apos;affichage</span>
                  <AcademiaSelect
                    fieldSize="sm"
                    value={currencyForm.displayMode}
                    onChange={(e) =>
                      setCurrencyForm((c) =>
                        c
                          ? {
                              ...c,
                              displayMode: e.target.value as AdminSystemCurrencySettings['displayMode'],
                            }
                          : c,
                      )
                    }
                  >
                    <option value="DEVISE_PRINCIPALE">Devise principale (CDF)</option>
                    <option value="DOUBLE_AFFICHAGE">Double affichage</option>
                  </AcademiaSelect>
                </label>

                <label className="tarif-field">
                  <span className="tarif-field__label">Taux USD → CDF</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={tarifInputClass}
                    value={currencyForm.usdToCdfRate}
                    onChange={(e) =>
                      setCurrencyForm((c) => (c ? { ...c, usdToCdfRate: Number(e.target.value) } : c))
                    }
                  />
                  <span className="mt-1 text-xs text-on-surface-variant">
                    Nombre de francs congolais pour 1 dollar américain.
                  </span>
                </label>

                <label className="tarif-field tarif-field--span-2">
                  <span className="tarif-field__label">Taux CDF → USD</span>
                  <input
                    type="number"
                    min={0}
                    step={0.000001}
                    className={tarifInputClass}
                    value={currencyForm.cdfToUsdRate}
                    onChange={(e) =>
                      setCurrencyForm((c) => (c ? { ...c, cdfToUsdRate: Number(e.target.value) } : c))
                    }
                  />
                  <span className="mt-1 text-xs text-on-surface-variant">Taux inverse (souvent une petite décimale).</span>
                </label>
              </div>

              <label className="currency-option-panel">
                <input
                  type="checkbox"
                  checked={recalculateTarifs}
                  onChange={(e) => setRecalculateTarifs(e.target.checked)}
                />
                <span className="currency-option-panel__text">
                  <span className="currency-option-panel__title">
                    Recalculer les tarifs actifs à partir des références USD
                  </span>
                  <span className="currency-option-panel__hint">
                    Applique le nouveau taux aux lignes tarifaires qui ont une référence USD enregistrée.
                  </span>
                </span>
              </label>
            </AcademicPanelCard>
          </div>
        ) : null}

        {activeSection === 'metier' ? (
          <div className="tarif-page-stack">
            <AcademicPanelCard
              icon="tune"
              title="Exploitation"
              subtitle="Options visibles côté administration et maintenance publique."
            >
              <div className="space-y-4">
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-outline-variant/20 bg-surface-container-lowest px-4 py-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form?.maintenanceMode ?? false}
                    onChange={(e) => updateField('maintenanceMode', e.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-on-surface">Mode maintenance</span>
                    <span className="mt-0.5 block text-xs text-on-surface-variant">
                      Bloque l’accès public à la plateforme le temps des interventions.
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-outline-variant/20 bg-surface-container-lowest px-4 py-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form?.pushNotifications ?? false}
                    onChange={(e) => updateField('pushNotifications', e.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-on-surface">Notifications push opérationnelles</span>
                    <span className="mt-0.5 block text-xs text-on-surface-variant">
                      Active l’envoi des alertes push côté administration.
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-outline-variant/20 bg-surface-container-lowest px-4 py-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form?.soundAlerts ?? false}
                    onChange={(e) => updateField('soundAlerts', e.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-on-surface">Alertes sonores</span>
                    <span className="mt-0.5 block text-xs text-on-surface-variant">
                      Joue un signal sonore lors de certaines alertes administrateur.
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-outline-variant/20 bg-surface-container-lowest px-4 py-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={form?.autoDiagnostics ?? false}
                    onChange={(e) => updateField('autoDiagnostics', e.target.checked)}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-on-surface">Auto-diagnostics</span>
                    <span className="mt-0.5 block text-xs text-on-surface-variant">
                      Active les contrôles automatiques de santé de la plateforme.
                    </span>
                  </span>
                </label>
              </div>
            </AcademicPanelCard>

            <AcademicPanelCard
              icon="groups"
              title="Parrainage"
              subtitle="Commissions % sur paiements formation — seuil = filleuls ayant payé. Bonus création de compte désactivé."
              badge={
                <span className="academic-panel-badge">
                  {(snapshot?.parrainage.regles ?? []).length} profil(s)
                </span>
              }
            >
              <div className="tarif-bonus-panel">
                <div className="tarif-bonus-panel__copy">
                  <p className="tarif-bonus-panel__label">
                    <LucideSymbolIcon name="stars" tone="inherit" size={18} className="text-primary" />
                    Bonus à la création de compte
                  </p>
                  <p className="tarif-bonus-panel__hint">
                    Désactivé (anti-fraude). Aucun crédit wallet tant que le filleul n&apos;a pas payé une
                    formation.
                  </p>
                </div>
                <label className="tarif-bonus-panel__field tarif-field max-w-xs">
                  <span className="tarif-field__label">Bonus (CDF) — inactif</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className={`${tarifInputClass} opacity-60`}
                    value="0"
                    disabled
                    readOnly
                  />
                </label>
              </div>

              <p className="tarif-subsection-title mt-6">
                Commissions (% des paiements formation — seuil filleuls payeurs)
              </p>
              <AcademicPanelTableWrap
                empty={
                  (snapshot?.parrainage.regles ?? []).length === 0 ? 'Aucune règle de commission.' : undefined
                }
              >
                <table className="academic-panel-table">
                  <thead>
                    <tr>
                      <th>Profil du parrain</th>
                      <th>Seuil filleuls payeurs</th>
                      <th>Taux commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(snapshot?.parrainage.regles ?? []).map((regle) => (
                      <ParrainageRegleRow
                        key={regle.id}
                        regle={regle}
                        draft={
                          regleDrafts[regle.id] ?? {
                            seuilFilleuls: String(regle.seuilFilleuls),
                            tauxCommissionPct: String(regle.tauxCommissionPct),
                          }
                        }
                        onDraftChange={(next) => setRegleDrafts((prev) => ({ ...prev, [regle.id]: next }))}
                      />
                    ))}
                  </tbody>
                </table>
              </AcademicPanelTableWrap>
            </AcademicPanelCard>
          </div>
        ) : null}

        {activeSection === 'documentation' ? (
          <div className="tarif-page-stack">
            <AcademicPanelCard
              icon="menu_book"
              title="Documentation utilisateur"
              subtitle="Guide PDF complet de toutes les fonctionnalités par rôle (public, apprenant, formateur, ambassadeur, partenaire, modérateur, administration, mobile)."
            >
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                <div className="flex shrink-0 items-center justify-center rounded-2xl border border-outline-variant/20 bg-gradient-to-br from-[#872320] to-[#6b1b18] p-6 shadow-md lg:w-56">
                  {previewLogoSrc ? (
                    <img src={previewLogoSrc} alt="" className="max-h-24 w-full object-contain" />
                  ) : (
                    <span className="text-center text-sm font-bold text-white">JCONSULT MY Academia</span>
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-4">
                  <p className="text-sm leading-relaxed text-on-surface-variant">
                    Document au format reçu officiel (bandeau bordeaux, logo institutionnel) regroupant l’ensemble des
                    modules web et mobile : connexion, catalogue public, portails apprenant / formateur / ambassadeur /
                    partenaire, administration et modération.
                  </p>
                  <ul className="grid gap-2 text-sm text-on-surface-variant sm:grid-cols-2">
                    <li className="flex items-center gap-2">
                      <LucideSymbolIcon name="check_circle" size={16} tone="primary" />
                      Tous les rôles et écrans
                    </li>
                    <li className="flex items-center gap-2">
                      <LucideSymbolIcon name="check_circle" size={16} tone="primary" />
                      Procédures pas à pas
                    </li>
                    <li className="flex items-center gap-2">
                      <LucideSymbolIcon name="check_circle" size={16} tone="primary" />
                      FAQ par espace
                    </li>
                    <li className="flex items-center gap-2">
                      <LucideSymbolIcon name="check_circle" size={16} tone="primary" />
                      Application mobile incluse
                    </li>
                  </ul>
                  <button
                    type="button"
                    disabled={isDownloadingUserGuide}
                    onClick={() => void handleDownloadUserGuide()}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-primary/90 disabled:opacity-60">
                    <LucideSymbolIcon name="download" size={18} tone="inherit" />
                    {isDownloadingUserGuide ? 'Génération du PDF…' : 'Télécharger la documentation PDF'}
                  </button>
                </div>
              </div>
            </AcademicPanelCard>
          </div>
        ) : null}

        {isDirty ? (
          <div className="sticky bottom-4 z-10 mt-8 flex flex-wrap items-center justify-end gap-3 rounded-xl border border-primary/20 bg-white/95 p-4 shadow-lg backdrop-blur">
            <p className="mr-auto text-sm text-on-surface-variant">Modifications non enregistrées</p>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg border border-outline-variant/30 px-4 py-2 text-sm font-semibold text-on-surface-variant hover:bg-surface-container-high"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={() => void handleSave()}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-primary/90 disabled:opacity-60"
            >
              {isSaving ? 'Enregistrement…' : 'Enregistrer les paramètres'}
            </button>
          </div>
        ) : null}
      </AdminPageShell>
    </>
  );
}
